import { cn } from "@/lib/cn";
import * as React from "react";
import Icon from "./icon";

const STYLES = {
  BASE: "tw-flex tw-w-full tw-resize-none tw-items-center tw-justify-start tw-rounded-md tw-outline tw-outline-1 tw-outline-blue/5 tw-bg-white tw-px-3 tw-py-1 tw-font-sans tw-text-blue-base tw-text-sm/none tw-font-normal placeholder:tw-text-blue-500 focus:tw-outline-violet-700",
  ERROR:
    "tw-outline-[#D43548] focus:tw-outline-[#D43548] tw-bg-[#FAF1F2] placeholder:tw-text-[#D43548]/80 tw-text-[#D43548]",
  DISABLED:
    "tw-bg-blue-100 tw-outline-blue-200 tw-text-blue-300 placeholder:tw-text-blue-300",
};

const SIZES = {
  default: "tw-h-9 tw-px-3 tw-text-sm/tight tw-rounded-lg",
  sm: "tw-h-8 tw-px-3 tw-py-[5px] tw-text-[11px]/normal",
  xs: "tw-h-6 tw-px-2 tw-py-1 tw-text-[11px]/normal",
  lg: "tw-h-12 tw-px-4 tw-py-3 tw-text-base/normal tw-rounded-lg",
};

const PADDING_OFFSET = {
  default: "tw-pl-11",
  sm: "tw-pl-10",
  xs: "tw-pl-8",
  lg: "tw-pl-12",
};

const ICON_POSITION = "tw-absolute tw-top-1/2 -tw-translate-y-1/2";

const ICON_STYLE = {
  default: "tw-size-5 tw-text-xl/none tw-left-4",
  sm: "tw-size-5 tw-text-xl/none tw-left-3",
  xs: "tw-size-4 tw-text-base/none tw-left-2",
  lg: "tw-size-6 tw-text-xl/none tw-left-4",
};

export type InputSize = keyof typeof SIZES;

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  size?: InputSize;
  error?: boolean;
  // A name the Icon primitive knows ("ri-search-line", "sd-…", a Material
  // ligature) or the icon itself — an element, for the Tabler icons the
  // newer screens use. Either way it sits inside the field, and the field
  // pads itself to make room.
  icon?: string | React.ReactNode;
  asChild?: boolean;
  iconClassName?: string;
  containerClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      iconClassName,
      containerClassName,
      type,
      size = "default",
      error,
      icon,
      ...props
    },
    ref,
  ) => {
    const [hasContent, setHasContent] = React.useState(false);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasContent(e.target.value.length > 0);
      props.onChange?.(e);
    };

    // `readOnly` inherits the disabled visual treatment (muted bg, dimmed
    // text, not-allowed cursor) so users can tell at a glance the field
    // isn't accepting input — the actual `readOnly` semantics are preserved
    // by passing the prop through, keeping the field focusable/copyable.
    const isInactive = props.disabled || props.readOnly;

    const iconColor = cn(
      hasContent ? "tw-fill-blue-base tw-text-blue-base" : "tw-fill-blue-500 tw-text-blue-500",
      error && "tw-fill-[#D43548] tw-text-[#D43548]",
      isInactive && "tw-fill-blue-300 tw-text-blue-300",
    );

    return (
      <div className={cn("tw-relative", containerClassName)}>
        <input
          type={type}
          className={cn(
            STYLES.BASE,
            SIZES[size],
            error && STYLES.ERROR,
            isInactive && STYLES.DISABLED,
            isInactive && "tw-cursor-not-allowed",
            Boolean(icon) && PADDING_OFFSET[size],
            className,
          )}
          onChange={handleChange}
          ref={ref}
          {...props}
        />

        {icon &&
          (typeof icon === "string" ? (
            <Icon
              icon={icon}
              className={cn(ICON_POSITION, iconColor, ICON_STYLE[size], iconClassName)}
            />
          ) : (
            // The element decides its own size; the wrapper only places it
            // and says what colour it should take.
            <span
              aria-hidden="true"
              className={cn(
                ICON_POSITION,
                "tw-flex tw-items-center tw-justify-center",
                iconColor,
                ICON_STYLE[size],
                iconClassName,
              )}
            >
              {icon}
            </span>
          ))}
      </div>
    );
  },
);
Input.displayName = "Input";
export default Input;
